import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

// Service imports
import { AuthService } from '../../../_services/auth.service';
import { UserService } from '../../../_services/data-services/user.service';
import { RPCacheRegistrationService } from '../../../_services/shared-services/rp-cache-registration-service';
import { DatadogService } from '../../../_services/datadog.service';
import { CustomAnalyticsService } from '../../../_services/shared-services/analytics.service';
import { EnvironmentConfigService } from '../../../_services/environment-config.service';

// Model imports
import { LoginParameterDto } from '../../../_models/login-parameter-dto';
import { UserPropertyResultDto } from '../../../_models/user-property-result-dto';
import { QuickPaymentPropertiesResultDto } from '../../../_models/quick-payment-properties-result-dto';
import { QuickPaymentPropertiesParameterDto } from '../../../_models/quick-payment-properties-parameter-dto';

// Local constants
import {
  QuickPayAnalyticsEvents,
  QuickPayAuthMethod,
  QuickPayStorageKeys
} from '../constants/quick-pay.constants';

/**
 * Service responsible for Quick Pay authentication orchestration.
 * Handles credentials login, Google OAuth, account number lookup,
 * and secure auth state cleanup.
 *
 * @description Follows Single Responsibility Principle - only handles auth concerns
 */
@Injectable({
  providedIn: 'root'
})
export class QuickPayAuthService {
  constructor(
    private authService: AuthService,
    private userService: UserService,
    private cacheService: RPCacheRegistrationService,
    private datadogService: DatadogService,
    private analyticsService: CustomAnalyticsService,
    private envConfig: EnvironmentConfigService
  ) {}

  /**
   * Checks if user is currently authenticated
   */
  get isAuthenticated(): boolean {
    return this.authService.isLoggedIn;
  }

  /**
   * Authenticates user with credentials for Quick Pay flow.
   * Does NOT emit login success to prevent app-level auth state change.
   *
   * @param credentials Login credentials
   * @returns Observable that completes on successful login
   */
  loginWithCredentials(credentials: LoginParameterDto): Observable<void> {
    // Clear any existing auth state before login
    this.authService.clearAuthLocalStorageData();

    return this.authService.login(credentials).pipe(
      tap(() => {
        this.trackAuthEvent(
          QuickPayAnalyticsEvents.AUTH_SUCCESS,
          QuickPayAuthMethod.CREDENTIALS
        );
      }),
      map(() => void 0),
      catchError((error) => {
        this.trackAuthEvent(
          QuickPayAnalyticsEvents.AUTH_FAILED,
          QuickPayAuthMethod.CREDENTIALS,
          error.error?.Message || 'Login failed'
        );
        return throwError(() => error);
      })
    );
  }

  /**
   * Initiates Google OAuth flow for Quick Pay.
   * Sets session flag and redirects to Google login.
   * The GoogleCallbackComponent will handle the return and fetch properties.
   */
  initiateGoogleOAuth(): void {
    this.trackAuthEvent(QuickPayAnalyticsEvents.GOOGLE_LOGIN_INITIATED);
    sessionStorage.setItem(QuickPayStorageKeys.GOOGLE_AUTH_FLAG, 'true');

    const spaOrigin = window.location.origin;
    const returnUrl = '/user/quickpayment';
    const googleLoginUrl = `${this.envConfig.ccAuthenticationApiUrl}google-login?portalType=RP&returnUrl=${encodeURIComponent(returnUrl)}&spaOrigin=${encodeURIComponent(spaOrigin)}`;

    window.location.href = googleLoginUrl;
  }

  /**
   * Fetches properties for authenticated user.
   * Immediately clears auth state after fetching - we only need the property data.
   *
   * @returns Observable of properties mapped to QuickPaymentPropertiesResultDto
   */
  fetchAuthenticatedUserProperties(): Observable<
    QuickPaymentPropertiesResultDto[]
  > {
    return this.userService.getUserProperties(true).pipe(
      tap(() => {
        // Clear auth state immediately after getting data
        // This is the critical security step - user should not remain logged in
        this.clearAuthState();
      }),
      map((userProperties: UserPropertyResultDto[]) =>
        this.mapToQuickPayProperties(userProperties)
      ),
      catchError((error) => {
        // Still clear auth state on error
        this.clearAuthState();
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetches properties by account number (unauthenticated flow).
   *
   * @param accountNumber 16-digit account number
   * @returns Observable of properties
   */
  fetchPropertiesByAccountNumber(
    accountNumber: string
  ): Observable<QuickPaymentPropertiesResultDto[]> {
    const param = new QuickPaymentPropertiesParameterDto(accountNumber);
    param.clearCache = true;

    return this.userService.getQuickPaymentProperties(param).pipe(
      tap((data) => {
        if (data.length > 0) {
          this.trackAuthEvent(
            QuickPayAnalyticsEvents.AUTH_SUCCESS,
            QuickPayAuthMethod.ACCOUNT_NUMBER
          );
        } else {
          this.trackAuthEvent(
            QuickPayAnalyticsEvents.AUTH_FAILED,
            QuickPayAuthMethod.ACCOUNT_NUMBER,
            'No properties data found'
          );
        }
      }),
      catchError((error) => {
        this.trackAuthEvent(
          QuickPayAnalyticsEvents.AUTH_FAILED,
          QuickPayAuthMethod.ACCOUNT_NUMBER,
          error.error?.message || 'Property retrieval failed',
          error.status
        );
        return throwError(() => error);
      })
    );
  }

  /**
   * Clears all authentication state.
   * This is critical for Quick Pay - user should not remain logged in after getting property data.
   *
   * Security Note: Cookie deletion may fail for HttpOnly cookies.
   * This is acceptable as the primary auth (JWT) is cleared from localStorage.
   */
  clearAuthState(): void {
    try {
      // 1. Clear JWT token and user info from localStorage
      this.authService.clearAuthLocalStorageData();

      // 2. Clear HTTP cache
      this.cacheService.deleteAllCache();

      // 3. Clear Datadog user tracking
      this.datadogService.clearUser();
    } catch (error) {
      // Log but don't throw - auth cleanup is best-effort
      // Primary auth (JWT in localStorage) is already cleared
      console.warn('Quick Pay auth cleanup partially failed:', error);
    }
  }

  /**
   * Maps UserPropertyResultDto to QuickPaymentPropertiesResultDto.
   * Centralizes the DTO transformation logic.
   */
  private mapToQuickPayProperties(
    userProperties: UserPropertyResultDto[]
  ): QuickPaymentPropertiesResultDto[] {
    return userProperties.map((prop) => {
      const quickPayProp = new QuickPaymentPropertiesResultDto();
      quickPayProp.communityId = prop.communityId;
      quickPayProp.communityName = prop.communityName;
      quickPayProp.customerId = prop.customerId;
      quickPayProp.propertyAddress = prop.propertyAddress;
      quickPayProp.allowOnlinePayment = true;
      return quickPayProp;
    });
  }

  /**
   * Tracks analytics events for Quick Pay authentication.
   */
  trackAuthEvent(
    event: string,
    method?: string,
    errorMessage?: string,
    errorCode?: number
  ): void {
    const payload: Record<string, unknown> = {};

    if (method) {
      payload['method'] = method;
    }
    if (errorMessage) {
      payload['error'] = errorMessage;
    }
    if (errorCode) {
      payload['error_reasonCode'] = errorCode;
    }

    this.analyticsService.trackEvent(event, payload);
  }
}
